<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { avatarText, getAvatarColor } from '@/modules/chat-matrix-fast/utils'
import { groupChatTypeLabel } from '@/modules/chat-matrix-fast/constants/chat'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { isGroupChat } from '@/modules/chat-matrix-fast/types/ChatMessage'

const props = defineProps<{
  modelValue: boolean
  chatId: number | null
  fallbackTitle?: string | null
  fallbackType?: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const store = useChatStore()
const coreStore = useChatStoreCore()
const feedbackStore = useFeedbackStore()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const isJoining = ref(false)

const localChat = computed(() => {
  if (!props.chatId) {
    return null
  }
  if (coreStore.currentChat?.id === props.chatId) {
    return coreStore.currentChat
  }
  const fromList = coreStore.chats.find(chat => chat.id === props.chatId)
  if (fromList) {
    return fromList
  }
  const active = store.activeChat?.chat
  if (active && isGroupChat(active) && active.id === props.chatId) {
    return active
  }
  return null
})

const title = computed(() =>
  localChat.value?.title?.trim()
  || props.fallbackTitle?.trim()
  || 'Группа',
)

const typeLabel = computed(() => {
  const type = localChat.value?.type || props.fallbackType || 'group'
  const label = groupChatTypeLabel(type).toLowerCase()
  return type === 'channel' ? label : `частная ${label}`
})

const icon = computed(() => {
  const chat = localChat.value
  if (chat && 'icon' in chat) {
    return chat.icon || null
  }
  return null
})

const isMember = computed(() => Boolean(localChat.value))

const joinButtonText = computed(() =>
  isMember.value ? 'Открыть группу' : 'Вступить в группу',
)

watch(
  () => props.modelValue,
  open => {
    if (!open) {
      isJoining.value = false
    }
  },
)

async function onJoin() {
  if (!props.chatId || isJoining.value) {
    return
  }

  isJoining.value = true
  try {
    if (!isMember.value) {
      const joined = await ChatApiService.joinByInvite(props.chatId)
      coreStore.upsertChatInList(joined)
    }

    await store.ensureChatListTabForChat({
      id: props.chatId,
      type: (localChat.value?.type as any) || props.fallbackType || 'group',
      title: title.value,
      description: '',
      icon: icon.value,
      owner: '',
      members: [],
      created_at: '',
    })
    await store.getChat(props.chatId)
    await store.fetchChatsAndContacts('')

    isOpen.value = false
  } catch (error) {
    debugConsoleError('Не удалось вступить в группу:', error)
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось вступить в группу. Нужен эндпоинт join-invite на бэкенде.',
      time: 5000,
    })
  } finally {
    isJoining.value = false
  }
}
</script>

<template>
  <VDialog
    v-model="isOpen"
    max-width="420"
    content-class="group-invite-join-dialog"
  >
    <VCard class="group-invite-join-card pa-6">
      <div class="d-flex justify-end mb-2">
        <CustomCloseButton :close-action="() => { isOpen = false }" />
      </div>

      <div class="d-flex flex-column align-center text-center">
        <VAvatar
          size="88"
          class="mb-4"
          :variant="!icon ? 'tonal' : undefined"
          :color="!icon ? 'primary' : undefined"
          :class="!icon ? getAvatarColor(title) : []"
        >
          <AuthenticatedVImg
            v-if="icon"
            :src="icon"
            :alt="title"
          />
          <span v-else>{{ avatarText(title) }}</span>
        </VAvatar>

        <h5 class="text-h5 mb-1">
          {{ title }}
        </h5>
        <p class="text-body-2 text-medium-emphasis mb-4">
          {{ typeLabel }}
        </p>
      </div>

      <VBtn
        class="mt-2"
        color="primary"
        block
        size="large"
        :loading="isJoining"
        @click="onJoin"
      >
        {{ joinButtonText }}
      </VBtn>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.group-invite-join-card {
  border-radius: 16px !important;
}

:deep(.v-avatar span) {
  font-weight: 600;
  color: #fff;
}
</style>
